import React from "react";
import styles from "./../RecordTable.module.scss";
import { AttributeLabels, ActionLabels } from "./Utils";

/**
 * SummaryTable component displays a summary table of attribute overrides.
 *
 * @param {Array} attributeMaps - An array of Maps containing attribute data.
 * @param {string} action - The action type (e.g., "replace", "inactive", "invalid", "restore").
 * @param {string} attribute - The attribute being modified (e.g., "identifier", "phone", "email").
 * @param {string} proposedValue - The proposed new value for the attribute.
 * @param {string} currentValue - The current value of the attribute.
 * @param {string} system - The system filter for identifier attributes.
 * @param {string} attrCode - The attribute code filter for multi-valued attributes.
 * @param {string} code - The code filter for identifier attributes without a system.
 * @returns {JSX.Element} - A React component that renders the summary table.
 */
const SummaryTable = ({
  attributeMaps,
  action,
  attribute,
  proposedValue,
  currentValue,
  system,
  attrCode,
  code,
}) => {
  /**
   * Generates a unique record identifier by combining the source and ID.
   *
   * @param {string} source - The source of the record.
   * @param {string} id - The ID of the record.
   * @returns {string} - A unique record identifier in the format "source | id
   */
  const getRecordIdFormat = (source, id) => {
    return source + " | " + id;
  };

  /**
   * A wrapper function that returns a styled span element with gray text.
   *
   * @param {string} text - The text to be displayed.
   * @param {string} title - The title attribute for the span element.
   * @returns {JSX.Element} - A styled span element.
   */
  const GrayValueWrapper = (text, title) => {
    return (
      <span className={styles.noValue} title={title}>
        {text}
      </span>
    );
  };

  /**
   * A wrapper function that returns a styled span element with gray text.
   *
   * @param {string} text - The text to be displayed.
   * @param {string} title - The title attribute for the span element.
   * @returns {JSX.Element} - A styled span element.
   */
  const GreenValueWrapper = (text, title) => {
    return (
      <span className={styles.matchingValue} title={title}>
        {text}
      </span>
    );
  };

  /**
   * Returns attribute Maps according to attribute and applicable filter items
   * to be used as values in the summary table.
   *
   * @returns {Array} - An array of attribute Maps.
   */
  const SummaryDisplayMaps = () => {
    var returnList = attributeMaps.flat();

    switch (attribute) {
      case AttributeLabels.BIRTHDATE:
      case AttributeLabels.GENDER:
        return returnList;
        break;

      case AttributeLabels.IDENTIFIER:
        if (code !== "" && code !== undefined) {
          return returnList.filter(
            (v) => v.get("code") === code && !v.has("system"),
          );
        } else {
          return returnList.filter((v) => v.get("system") === system);
        }
        break;

      case AttributeLabels.ADDRESS:
      case AttributeLabels.NAME:
      case AttributeLabels.PHONE:
      case AttributeLabels.EMAIL:
        return returnList.filter((v) => v.get("attrCode") === attrCode);
        break;
    }
  };

  /**
   * An enumeration for the different column types in the summary table.
   */
  const ColumnType = Object.freeze({
    RECORD_ID: "recordId",
    CURRENT_VALUE: "currentValue",
    OVERRIDE_TYPE: "overrideType",
    PROPOSED_VALUE: "proposedValue",
  });

  /**
   * An enumeration for descriptions of various display values used in the summary table.
   */
  const DescriptionMap = Object.freeze({
    DESC_01:
      "The record does not contain the given attribute and filter items.",
    DESC_02: "The value is inactive.",
    DESC_03: "The current value is an override.",
    DESC_04: "The current value has not been overridden.",
    DESC_05: 'The current value does not match the selected "Value to Modify".',
    DESC_06: "The value to override does not exist.",
    DESC_07: "The value to inactivate does not exist.",
    DESC_08: "The value to invalidate does not exist.",
    DESC_09: "The value to restore does not exist.",
    DESC_10: "The value to inactivate is already inactive.",
    DESC_11: "The proposed value is the same as the current value.",
    VAL_01: "[No Change]",
    VAL_02: "Value will be inactivated.",
    VAL_03: "Value will be invalidated.",
    VAL_04: "Overridden value will be restored.",
    VAL_05: "[No Change - Override Present]",
    VAL_06: "[No Change - Inactive]",
  });

  /**
   * Determines the summary display value for a given record based on the column type.
   *
   * @param {Map} attributeMap - A Map containing the attributes of the record.
   * @param {string} columnType - The type of column being displayed (e.g., "recordId", "currentValue", "proposedValue").
   * @returns {string} - The display value for the specified column type.
   */
  const getSummaryDisplayValue = (attributeMap, columnType) => {
    switch (columnType) {
      case ColumnType.RECORD_ID:
        return attributeMap?.has("source") && attributeMap?.has("id")
          ? getRecordIdFormat(
              attributeMap.get("source"),
              attributeMap.get("id"),
            )
          : "";
      case ColumnType.CURRENT_VALUE:
        return attributeMap?.has("displayValue")
          ? attributeMap.get("displayValue")
          : "";
      case ColumnType.PROPOSED_VALUE:
        return proposedValue;
    }
  };

  /**
   * Determines the display value and description for a given record for Replace action type.
   *
   * @param {string} recordId - The unique identifier for the record.
   * @param {Map} attributeMap - A Map containing the attributes of the record.
   * @param {string} columnType - The type of column being displayed (e.g., "recordId", "currentValue", "previousValue", "proposedValue").
   * @returns {JSX.Element} - A styled span element containing the display value and its corresponding description as a tooltip.
   */
  const getGenericDisplayWithDescription = (
    recordId,
    attributeMap,
    columnType,
    action,
    displayValue,
    placeholderValue,
  ) => {
    if (typeof attributeMap === "undefined") {
      return GrayValueWrapper(
        placeholderValue || displayValue,
        DescriptionMap.DESC_01,
      );
    }
    if (attributeMap.has("overrideType") && action !== ActionLabels.RESTORE) {
      return GrayValueWrapper(displayValue, DescriptionMap.DESC_03);
    }
    if (
      attributeMap.get("use")?.toUpperCase() === "OLD" &&
      ![ActionLabels.RESTORE, ActionLabels.INVALID].includes(action)
    ) {
      return GrayValueWrapper(displayValue, DescriptionMap.DESC_02);
    }
    if (attributeMap.has("overrideType") && action !== ActionLabels.RESTORE) {
      return GrayValueWrapper(displayValue, DescriptionMap.DESC_03);
    }
    if (attributeMap.get("displayValue") === proposedValue) {
      return GrayValueWrapper(displayValue, DescriptionMap.DESC_11);
    }
    if (attributeMap.get("displayValue") !== currentValue) {
      return GrayValueWrapper(displayValue, DescriptionMap.DESC_05);
    }
    if (
      action === ActionLabels.RESTORE &&
      !attributeMap.has("overridePresent")
    ) {
      return GrayValueWrapper(displayValue, DescriptionMap.DESC_04);
    }

    return displayValue;
  };

  /**
   * Determines the display value and description for a given record for Replace action type.
   *
   * @param {string} recordId - The unique identifier for the record.
   * @param {Map} attributeMap - A Map containing the attributes of the record.
   * @param {string} columnType - The type of column being displayed (e.g., "recordId", "currentValue", "previousValue", "proposedValue").
   * @returns {JSX.Element} - A styled span element containing the display value and its corresponding description as a tooltip.
   */
  const getReplaceDisplayWithDescription = (
    recordId,
    attributeMap,
    columnType,
  ) => {
    if (typeof attributeMap === "undefined") {
      return GrayValueWrapper(DescriptionMap.VAL_01, DescriptionMap.DESC_01);
    }
    if (attributeMap.has("overrideType")) {
      return GrayValueWrapper(DescriptionMap.VAL_05, DescriptionMap.DESC_03);
    }
    if (attributeMap.get("use")?.toUpperCase() === "OLD") {
      return GrayValueWrapper(DescriptionMap.VAL_06, DescriptionMap.DESC_02);
    }
    if (attributeMap.get("displayValue") === proposedValue) {
      return GrayValueWrapper(DescriptionMap.VAL_01, DescriptionMap.DESC_05);
    }
    if (attributeMap.get("displayValue") !== currentValue) {
      return GrayValueWrapper(DescriptionMap.VAL_01, DescriptionMap.DESC_05);
    }

    return GreenValueWrapper(proposedValue);
  };

  /**
   * Determines the display value and description for a given record for Inactivate action type.
   *
   * @param {string} recordId - The unique identifier for the record.
   * @param {Map} attributeMap - A Map containing the attributes of the record.
   * @param {string} columnType - The type of column being displayed (e.g "recordId", "currentValue", "previousValue", "proposedValue").
   * @returns {JSX.Element} - A styled span element containing the display value and its corresponding description as a tooltip.
   */
  const getInactiveDisplayWithDescription = (
    recordId,
    attributeMap,
    columnType,
  ) => {
    if (typeof attributeMap === "undefined") {
      return GrayValueWrapper(DescriptionMap.VAL_01, DescriptionMap.DESC_07);
    }
    if (attributeMap.has("overrideType")) {
      return GrayValueWrapper(DescriptionMap.VAL_05, DescriptionMap.DESC_03);
    }
    if (attributeMap.get("displayValue") !== currentValue) {
      return GrayValueWrapper(DescriptionMap.VAL_01, DescriptionMap.DESC_05);
    }
    if (attributeMap.get("use")?.toUpperCase() === "OLD") {
      return GrayValueWrapper(DescriptionMap.VAL_01, DescriptionMap.DESC_10);
    }

    return GreenValueWrapper(DescriptionMap.VAL_02);
  };

  /**
   * Determines the display value and description for a given record for Invalidate action type.
   *
   * @param {string} recordId - The unique identifier for the record.
   * @param {Map} attributeMap - A Map containing the attributes of the record.
   * @param {string} columnType - The type of column being displayed (e.g "recordId", "currentValue", "previousValue", "proposedValue").
   * @returns {JSX.Element} - A styled span element containing the display value and its corresponding description as a tooltip.
   */
  const getInvalidDisplayWithDescription = (
    recordId,
    attributeMap,
    columnType,
  ) => {
    if (typeof attributeMap === "undefined") {
      return GrayValueWrapper(DescriptionMap.VAL_01, DescriptionMap.DESC_08);
    }
    if (attributeMap.has("overrideType")) {
      return GrayValueWrapper(DescriptionMap.VAL_01, DescriptionMap.DESC_03);
    }
    if (attributeMap.get("displayValue") !== currentValue) {
      return GrayValueWrapper(DescriptionMap.VAL_01, DescriptionMap.DESC_05);
    }

    return GreenValueWrapper(DescriptionMap.VAL_03);
  };

  /**
   * Determines the display value and description for a given record for Restore action type.
   *
   * @param {string} recordId - The unique identifier for the record.
   * @param {Map} attributeMap - A Map containing the attributes of the record.
   * @param {string} columnType - The type of column being displayed (e.g "recordId", "currentValue", "previousValue", "proposedValue").
   * @returns {JSX.Element} - A styled span element containing the display value and its corresponding description as a tooltip.
   */
  const getRestoreDisplayWithDescription = (
    recordId,
    attributeMap,
    columnType,
  ) => {
    const restoreValue = attributeMap?.has("overridePreviousDisplayValue")
      ? attributeMap.get("overridePreviousDisplayValue")
      : "";

    if (typeof attributeMap === "undefined") {
      return GrayValueWrapper(DescriptionMap.VAL_01, DescriptionMap.DESC_09);
    }
    if (attributeMap.get("displayValue") !== currentValue) {
      return GrayValueWrapper(DescriptionMap.VAL_01, DescriptionMap.DESC_05);
    }
    if (!attributeMap.has("overrideType")) {
      return GrayValueWrapper(DescriptionMap.VAL_01, DescriptionMap.DESC_04);
    }

    return GreenValueWrapper(restoreValue);
  };

  /**
   * Determines the display value and description for a given record based on the action type.
   *
   * @param {string} recordId - The unique identifier for the record.
   * @param {Map} attributeMap - A Map containing the attributes of the record.
   * @param {string} action - The type of action being performed (e.g., "replace", "inactive", "invalid", "restore").
   * @param {string} columnType - The type of column being displayed (e.g., "recordId", "currentValue", "previousValue", "proposedValue").
   * @returns {JSX.Element} - A styled span element containing the display value and its corresponding description as a tooltip based on the action type.
   */
  const getProposedValueWithDescription = (
    recordId,
    attributeMap,
    columnType,
    action,
  ) => {
    switch (action) {
      case ActionLabels.REPLACE:
        return getReplaceDisplayWithDescription(
          recordId,
          attributeMap,
          columnType,
        );
      case ActionLabels.INACTIVE:
        return getInactiveDisplayWithDescription(
          recordId,
          attributeMap,
          columnType,
        );
      case ActionLabels.INVALID:
        return getInvalidDisplayWithDescription(
          recordId,
          attributeMap,
          columnType,
        );
      case ActionLabels.RESTORE:
        return getRestoreDisplayWithDescription(
          recordId,
          attributeMap,
          columnType,
        );
    }
  };

  /**
   * Determines the summary display value for a given record based on the column type.
   *
   * @param {string} columnType - The type of column being displayed (e.g., "recordId", "currentValue", "proposedValue").
   * @param {string} recordId - The unique identifier for the record.
   * @param {Map} attributeMap - A Map containing the attributes of the record.
   * @returns {string} - The display value for the specified column type.
   */
  const getDisplayValue = (columnType, recordId, attributeMap) => {
    switch (columnType) {
      case ColumnType.RECORD_ID:
        return recordId;
      case ColumnType.CURRENT_VALUE:
        return attributeMap?.has("displayValue")
          ? attributeMap.get("displayValue")
          : "";
      case ColumnType.OVERRIDE_TYPE:
        return attributeMap?.has("overrideType")
          ? attributeMap.get("overrideType").toUpperCase()
          : "";
      default:
        return "";
    }
  };

  /**
   * Determines the placeholder value for a given column type.
   *
   * @param {string} columnType - The type of column being displayed (e.g., "recordId", "currentValue", "proposedValue").
   * @returns {string} - The placeholder value for the specified column type.
   */
  const getPlaceholderValue = (columnType) => {
    switch (columnType) {
      case ColumnType.RECORD_ID:
        return "";
      default:
        return DescriptionMap.VAL_01;
    }
  };

  /**
   * Determines the display value and description for a given record based on the action type.
   * @param {string} recordId - The unique identifier for the record.
   * @param {Map} attributeMap - A Map containing the attributes of the record.
   * @param {string} action - The type of action being performed (e.g., "replace", "inactive", "invalid", "restore").
   * @param {string} columnType - The type of column being displayed (e.g., "recordId", "currentValue", "previousValue", "proposedValue").
   * @returns {JSX.Element} - A styled span element containing the display value and its corresponding description as a tooltip based on the action type.
   */
  const getDisplayWithDescription = (
    recordId,
    attributeMap,
    action,
    columnType,
  ) => {
    switch (columnType) {
      case ColumnType.PROPOSED_VALUE:
        return getProposedValueWithDescription(
          recordId,
          attributeMap,
          columnType,
          action,
        );
      default:
        const displayValue = getDisplayValue(
          columnType,
          recordId,
          attributeMap,
        );
        const placeholderValue = getPlaceholderValue(columnType);

        return getGenericDisplayWithDescription(
          recordId,
          attributeMap,
          columnType,
          action,
          displayValue,
          placeholderValue,
        );
    }
  };

  /**
   * Groups attribute maps by their record id and filters based on attrCode selected.
   */
  const groupedByRecordId = attributeMaps.flat().reduce((acc, attrMap) => {
    const recordId = getRecordIdFormat(
      attrMap.get("source"),
      attrMap.get("id"),
    );

    const addToRecordGroup = (acc, recordId, attrMap) => {
      if (!acc[recordId]) acc[recordId] = [];
      acc[recordId].push(attrMap);
    };

    switch (attribute) {
      case AttributeLabels.BIRTHDATE:
      case AttributeLabels.GENDER:
        addToRecordGroup(acc, recordId, attrMap);
        break;
      case AttributeLabels.IDENTIFIER:
        if (attrMap.get("system") === system) {
          addToRecordGroup(acc, recordId, attrMap);
        }
        if (
          system === "[No System]" &&
          !attrMap.get("system") &&
          attrMap.get("code") === code
        ) {
          addToRecordGroup(acc, recordId, attrMap);
        }
        if (
          action === ActionLabels.RESTORE &&
          attrMap?.get("overrideType") === ActionLabels.INVALID
        ) {
          addToRecordGroup(acc, recordId, attrMap);
        }
        break;
      default:
        if (attrMap.get("attrCode") === attrCode) {
          addToRecordGroup(acc, recordId, attrMap);
        }
        break;
    }

    return acc;
  }, {});

  /**
   * Label for the proposed value column based on the action type.
   *
   * @param {string} action - The type of action being performed (e.g., "replace", "inactive", "invalid", "restore").
   * @returns {string} - The label for the proposed value column.
   */
  const getProposedValueColumnLabel = (action) => {
    switch (action) {
      case ActionLabels.RESTORE:
        return "Restore Value";
      default:
        return "Proposed New Value";
    }
  };

  return (
    <div className={styles.overrideSummaryTable}>
      <table>
        <thead>
          <tr>
            <th>Source | Record ID</th>
            <th></th>
            <th>Current Value</th>
            <th></th>
            {action === ActionLabels.RESTORE && (
              <>
                <th>Override Type</th>
                <th></th>
              </>
            )}
            <th>{getProposedValueColumnLabel(action)}</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(groupedByRecordId).map(([recordId, attrMaps]) =>
            attrMaps.map((attributeMap, idx) => (
              <tr key={recordId + "-" + idx} style={{ height: "2rem" }}>
                <td>
                  {getDisplayWithDescription(
                    recordId,
                    attributeMap,
                    action,
                    ColumnType.RECORD_ID,
                  )}
                </td>
                <td></td>
                <td>
                  {getDisplayWithDescription(
                    recordId,
                    attributeMap,
                    action,
                    ColumnType.CURRENT_VALUE,
                  )}
                </td>
                <td></td>
                {action === ActionLabels.RESTORE && (
                  <>
                    <td>
                      {getDisplayWithDescription(
                        recordId,
                        attributeMap,
                        action,
                        ColumnType.OVERRIDE_TYPE,
                      )}
                    </td>
                    <td></td>
                  </>
                )}
                <td>
                  {getDisplayWithDescription(
                    recordId,
                    attributeMap,
                    action,
                    ColumnType.PROPOSED_VALUE,
                  )}
                </td>
              </tr>
            )),
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SummaryTable;
